// Action: what to do about each problem Insight found ("dari masalah yang
// didapat di insight action plannya itu harus bagaimana"). The plan comes
// from GET /api/i/:slug/actions, ordered by priority, each row naming the
// insight it answers. Status can be changed here, but there is no API write
// yet, so the page says plainly that a change lives only on this screen.
//
// Section concepts (JEV ui.region_gate, 2026-10-12):
//   Catatan lokal  job: know a status change is not saved.                   info Notice.
//   Ringkasan      one level row: Kemajuan (status donut, done of total),
//                  Prioritas (status per priority, a view switch to effort), Pemilik
//                  (actions per owner, bars). A single owner drops the owner card.
//   Rencana        job: work through the plan and change a status.           rows in one flush
//                  panel (container rows, 0.85), filter chips in its toolbar.
//   Rincian        job: read the full wording behind an action.              one closed Disclosure.
//
// Every summary figure is a count of the actions the API returned, after the
// status changes the reader made here; nothing is invented.
import { useEffect, useId, useMemo, useState, type ReactNode } from "react";
import {
  WaffleChart,
  DonutChart,
  Disclosure,
  EmptyState,
  FilterChips,
  Notice,
  PageHeader,
  Panel,
  StackedColumns,
  VerdictBadge,
  ViewSwitch,
  VizCard,
  VizFigure,
  VizRow,
} from "@dashboard-intelligent/ui";
import type { ActionEffort, ActionItem, ActionStatus, Insight, Verdict } from "@dashboard-intelligent/api/src/modules/industry/types";
import { endpoints } from "../endpoints";
import { useIndustry } from "../industry";
import { useApi } from "../useApi";
import { VERDICT_TEXT, formatMetric } from "../format";
import {
  EFFORT_ORDER,
  EFFORT_TEXT,
  STATUS_ORDER,
  STATUS_TEXT,
  byPriority,
  countByEffort,
  countByStatus,
  filterPlan,
  ownerLoad,
  ownersOf,
  priorityBands,
  reachText,
  verdictsOf,
} from "./action/plan";
import { tenantPath } from "../tenant";

const PANEL_TITLE = "Rencana tindakan";

export function ActionPage() {
  const { slug } = useIndustry();
  const plan = useApi(() => Promise.all([endpoints.actions(slug), endpoints.insights(slug)]));

  return (
    <>
      <PageHeader
        title="Action"
        description="Apa yang dikerjakan untuk setiap masalah yang ditemukan di Insight, diurutkan dari prioritas tertinggi."
      />
      {plan.status === "loading" ? (
        <Panel title={PANEL_TITLE}>
          <div aria-busy="true">
            <EmptyState icon="action" title="Memuat rencana tindakan">
              Rencana diambil dari data industri ini.
            </EmptyState>
          </div>
        </Panel>
      ) : plan.status === "error" ? (
        <Notice
          tone="danger"
          title="Rencana tindakan tidak dapat dimuat"
          action={
            <button type="button" className="btn-secondary" onClick={plan.reload}>
              Coba lagi
            </button>
          }
        >
          <p>{plan.error}</p>
        </Notice>
      ) : (
        <Plan actions={plan.data[0]} insights={plan.data[1]} />
      )}
    </>
  );
}

function Plan({ actions, insights }: { actions: ActionItem[]; insights: Insight[] }) {
  const ordered = useMemo(() => byPriority(actions), [actions]);
  const insightById = useMemo(() => new Map(insights.map((insight) => [insight.id, insight])), [insights]);
  const owners = useMemo(() => ownersOf(ordered), [ordered]);
  const verdicts = useMemo(() => verdictsOf(ordered, insightById), [ordered, insightById]);

  const [ownerFilter, setOwnerFilter] = useState<string[]>([]);
  const [verdictFilter, setVerdictFilter] = useState<Verdict[]>([]);
  // Local only: no API write exists for an action's status.
  const [changed, setChanged] = useState<Record<string, ActionStatus>>({});
  const statusOf = (action: ActionItem): ActionStatus => changed[action.id] ?? action.status;

  // /action#<id> from Insight lands on that action.
  useEffect(() => {
    if (typeof location === "undefined" || !location.hash) return;
    const id = decodeURIComponent(location.hash.slice(1));
    if (!ordered.some((action) => action.id === id)) return;
    document.getElementById(id)?.scrollIntoView({ block: "start" });
  }, [ordered]);

  if (ordered.length === 0) {
    return (
      <Panel title={PANEL_TITLE}>
        <EmptyState icon="action" title="Belum ada rencana tindakan">
          Industri ini belum punya temuan Insight yang dinilai, jadi belum ada tindakan untuk direncanakan. Rencana muncul di sini begitu definisi metriknya tersedia.
        </EmptyState>
      </Panel>
    );
  }

  const shown = filterPlan(ordered, insightById, ownerFilter, verdictFilter);
  const filtered = ownerFilter.length > 0 || verdictFilter.length > 0;
  const reset = () => {
    setOwnerFilter([]);
    setVerdictFilter([]);
  };

  return (
    <>
      <Notice tone="info" title="Status hanya berubah di layar ini">
        <p>Perubahan status belum disimpan ke server dan hilang saat halaman dimuat ulang atau dibuka di perangkat lain.</p>
      </Notice>
      <Summary actions={ordered} statusOf={statusOf} />
      <Panel
        title={PANEL_TITLE}
        variant="flush"
        toolbar={
          <>
            <ChipGroup label="Pemilik">
              <FilterChips
                label="Saring menurut pemilik"
                options={owners.map((owner) => ({ value: owner, label: owner }))}
                value={ownerFilter}
                onChange={setOwnerFilter}
              />
            </ChipGroup>
            {verdicts.length > 1 ? (
              <ChipGroup label="Penilaian temuan">
                <FilterChips<Verdict>
                  label="Saring menurut penilaian temuan"
                  options={verdicts.map((verdict) => ({ value: verdict, label: VERDICT_TEXT[verdict] }))}
                  value={verdictFilter}
                  onChange={setVerdictFilter}
                />
              </ChipGroup>
            ) : null}
          </>
        }
        footer={
          <p>
            Menampilkan {formatMetric(shown.length, "count")} dari {formatMetric(ordered.length, "count")} tindakan.
          </p>
        }
      >
        {shown.length === 0 ? (
          <EmptyState
            icon="action"
            title="Tidak ada tindakan yang cocok"
            action={
              <button type="button" className="btn-secondary" onClick={reset}>
                Hapus saringan
              </button>
            }
          >
            Tidak ada tindakan untuk pemilik dan penilaian yang dipilih.
          </EmptyState>
        ) : (
          <ul className="con-list" aria-label={filtered ? `${PANEL_TITLE}, disaring` : PANEL_TITLE}>
            {shown.map((action) => (
              <ActionRow
                key={action.id}
                action={action}
                insight={insightById.get(action.insightId) ?? null}
                status={statusOf(action)}
                onStatus={(next) => setChanged((current) => ({ ...current, [action.id]: next }))}
              />
            ))}
          </ul>
        )}
      </Panel>
      <Details actions={ordered} />
    </>
  );
}

/** A whole number of actions, printed the way every page prints a count. */
const count = (n: number) => formatMetric(n, "count");

/**
 * The state of the plan as one level row: how far it is, where the open work
 * sits by priority (or by effort), and who carries it. One owner is no
 * breakdown, so with one owner the row holds the first two cards.
 */
function Summary({ actions, statusOf }: { actions: ActionItem[]; statusOf: (action: ActionItem) => ActionStatus }) {
  const load = ownerLoad(actions, statusOf);
  const owners = load.length >= 2;
  const span = owners ? 4 : 6;
  return (
    <VizRow label="Ringkasan rencana tindakan">
      <ProgressCard span={span} counts={countByStatus(actions, statusOf)} total={actions.length} />
      <PriorityCard span={span} actions={actions} statusOf={statusOf} />
      {owners ? <OwnerCard span={4} actions={actions} statusOf={statusOf} /> : null}
    </VizRow>
  );
}

/** How far the plan is: the done count, and the three statuses as parts of the plan. */
function ProgressCard({ span, counts, total }: { span: 4 | 6; counts: Record<ActionStatus, number>; total: number }) {
  return (
    <VizCard
      span={span}
      title="Kemajuan rencana"
      description={`${count(total)} tindakan, status saat ini`}
      figure={<VizFigure value={`${count(counts.done)} dari ${count(total)}`} label="tindakan selesai" />}
      cardId="progress"
    >
      <WaffleChart
        label="Tindakan per status"
        data={STATUS_ORDER.map((status) => ({ label: STATUS_TEXT[status], value: counts[status] }))}
        format={count}
        categoryLabel="Status"
        valueLabel="Tindakan"
        unit="count"
      />
    </VizCard>
  );
}

type PriorityView = "priority" | "effort";

/** Where the open work sits: statuses per priority, or actions per effort level. */
function PriorityCard({ span, actions, statusOf }: { span: 4 | 6; actions: ActionItem[]; statusOf: (action: ActionItem) => ActionStatus }) {
  const [view, setView] = useState<PriorityView>("priority");
  const bands = priorityBands(actions, statusOf);
  const top = bands[0];
  const left = top ? top.total - top.counts.done : 0;
  const efforts = countByEffort(actions);
  const heaviest = EFFORT_ORDER.reduce((best, e) => (efforts[e] > efforts[best] ? e : best), EFFORT_ORDER[0]!);
  return (
    <VizCard
      span={span}
      title={view === "priority" ? "Status per prioritas" : "Tindakan per tingkat usaha"}
      description={view === "priority" ? `${count(bands.length)} tingkat prioritas` : "Rendah, sedang, tinggi"}
      controls={
        <ViewSwitch<PriorityView>
          label="Tampilan ringkasan"
          options={[
            { value: "priority", label: "Prioritas" },
            { value: "effort", label: "Usaha" },
          ]}
          value={view}
          onChange={setView}
        />
      }
      figure={
        view === "priority" ? (
          <VizFigure value={count(left)} label={top ? `tindakan prioritas ${top.words} belum selesai` : "tindakan belum dikelompokkan"} />
        ) : (
          <VizFigure value={count(efforts[heaviest])} label={`tindakan usaha ${EFFORT_TEXT[heaviest].toLowerCase()}, terbanyak`} />
        )
      }
      cardId="priority"
    >
      <div className="viz-view" key={view}>
        {view === "priority" ? (
          <StackedColumns
            label="Tindakan per status dan prioritas"
            categoryLabel="Prioritas"
            format={count}
            data={bands.map((band) => ({ label: `Prioritas ${band.label}`, ...band.counts }))}
            series={STATUS_ORDER.map((status) => ({ key: status, label: STATUS_TEXT[status] }))}
          />
        ) : (
          <StackedColumns
            label="Tindakan per tingkat usaha"
            categoryLabel="Usaha"
            format={count}
            data={EFFORT_ORDER.map((effort) => ({ label: EFFORT_TEXT[effort], value: efforts[effort] }))}
            series={[{ key: "value", label: "Tindakan" }]}
          />
        )}
      </div>
    </VizCard>
  );
}

/** Who carries the plan: actions per owner, heaviest first. */
function OwnerCard({ span, actions, statusOf }: { span: 4; actions: ActionItem[]; statusOf: (action: ActionItem) => ActionStatus }) {
  const load = ownerLoad(actions, statusOf);
  const done = load.reduce((s, row) => s + row.done, 0);
  const top = load[0]!;
  return (
    <VizCard
      span={span}
      title="Beban per pemilik"
      description={`${count(load.length)} pemilik, ${count(done)} tindakan selesai`}
      figure={<VizFigure value={count(top.total)} label={`tindakan, terbanyak ${top.owner}`} />}
      cardId="owners"
    >
      <DonutChart
        label="Tindakan per pemilik"
        data={load.map((row) => ({ label: row.owner, value: row.total }))}
        format={count}
        categoryLabel="Pemilik"
        valueLabel="Tindakan"
      />
    </VizCard>
  );
}

function ChipGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="field">
      <p className="field-label">{label}</p>
      {children}
    </div>
  );
}

function ActionRow({
  action,
  insight,
  status,
  onStatus,
}: {
  action: ActionItem;
  insight: Insight | null;
  status: ActionStatus;
  onStatus: (status: ActionStatus) => void;
}) {
  const selectId = useId();
  const hintId = useId();
  const reach = reachText(action.reach);
  const edited = status !== action.status;

  return (
    <li id={action.id} className="con-list-item">
      <div className="con-list-row">
        <div className="con-list-text">
          <p className="con-list-title">{action.title}</p>
          <p className="con-record-meta">
            <span>Prioritas {formatMetric(action.priority, "count")}</span>
            <span>Pemilik: {action.owner}</span>
            <span>Usaha: {EFFORT_TEXT[action.effort]}</span>
            {reach ? <span>Jangkauan: {reach}</span> : null}
          </p>
          <p className="viz-inline">
            {insight ? (
              <>
                <VerdictBadge verdict={insight.verdict} />
                <span>{insight.title}</span>
              </>
            ) : (
              <span>Temuan yang dijawab tidak ditemukan di Insight.</span>
            )}
          </p>
        </div>
        <div className="con-list-trailing">
          <div className="field">
            <label className="field-label" htmlFor={selectId}>
              Status
            </label>
            <select
              id={selectId}
              value={status}
              aria-describedby={edited ? hintId : undefined}
              onChange={(event) => onStatus(event.target.value as ActionStatus)}
            >
              {STATUS_ORDER.map((option) => (
                <option key={option} value={option}>
                  {STATUS_TEXT[option]}
                </option>
              ))}
            </select>
            {edited ? (
              <p className="field-hint" id={hintId}>
                Belum disimpan
              </p>
            ) : null}
          </div>
          {insight ? (
            <a className="btn btn-secondary" href={`${tenantPath("/insight")}#${encodeURIComponent(insight.id)}`} aria-label={`Lihat temuan "${insight.title}" di Insight`}>
              Lihat temuan
            </a>
          ) : null}
        </div>
      </div>
    </li>
  );
}

/**
 * Every line of prose the rows no longer show, one tab stop away: per action
 * the plan in full, the target it aims at, and the drivers it moves.
 */
function Details({ actions }: { actions: ActionItem[] }) {
  return (
    <Disclosure title="Rincian tiap tindakan" description="Rencana lengkap, target, dan driver untuk setiap tindakan.">
      {actions.map((action) => (
        <div className="viz-detail" key={action.id} data-action={action.id}>
          <h3 className="viz-detail-title">
            Prioritas {formatMetric(action.priority, "count")}: {action.title}
          </h3>
          <p className="viz-detail-text">{action.detail}</p>
          <p className="viz-detail-text">Target: {action.target}</p>
          {action.drivers.length > 0 ? <p className="viz-detail-text">Driver: {action.drivers.join(", ")}</p> : null}
        </div>
      ))}
    </Disclosure>
  );
}
